import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Leaf, Sparkles, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const freePerks = [
  'Full herb & oil library',
  'All 52 guided recipes',
  'Basic first-aid kit builder',
  'Safety & contraindication notes',
  'AI botanical illustrations — free',
  '1 free Sage message per day'
];

const premiumPerks = [
  'Everything in Free',
  'Unlimited Sage AI conversations',
  'Personalised family profiles',
  'Advanced safety cross-checking',
  'Smart shopping lists & reminders',
  'Offline access & new recipes first'
];

export default function Membership() {
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();

  const handleFree = () => {
    if (!user) { openAuth('register'); return; }
    navigate('/');
  };

  const handlePremium = () => {
    if (!user) { openAuth('register'); return; }
    navigate('/checkout');
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-14 min-h-[70vh]">
      <div className="text-center max-w-xl mx-auto mb-12">
        <p className="text-[11px] uppercase tracking-[0.2em] text-botanica-green font-semibold mb-3">Membership</p>
        <h1 className="font-serif text-4xl md:text-5xl text-botanica-ink leading-tight mb-4">
          Simple, gentle pricing.
        </h1>
        <p className="text-botanica-muted leading-relaxed">
          Start free and explore everything. Upgrade whenever you want unlimited Sage and personalised care — cancel anytime.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-start">
        {/* Free */}
        <div className="bg-botanica-card/60 rounded-3xl p-8 border border-botanica-card">
          <span className="w-11 h-11 rounded-xl bg-botanica-greenLight flex items-center justify-center mb-5">
            <Leaf className="w-5 h-5 text-botanica-green" />
          </span>
          <h2 className="font-serif text-2xl text-botanica-ink mb-1">Free</h2>
          <p className="text-sm text-botanica-muted mb-5">For curious beginners.</p>
          <div className="mb-6">
            <span className="font-serif text-4xl text-botanica-ink">$0</span>
            <span className="text-sm text-botanica-muted"> / forever</span>
          </div>
          <ul className="space-y-3 mb-8">
            {freePerks.map((p) => (
              <li key={p} className="flex items-start gap-2.5 text-sm text-botanica-ink">
                <Check className="w-4 h-4 text-botanica-green mt-0.5 shrink-0" /> {p}
              </li>
            ))}
          </ul>
          <button
            onClick={handleFree}
            className="w-full py-3 rounded-full border border-botanica-green text-botanica-greenDark font-medium hover:bg-botanica-greenLight transition-colors"
          >
            {user ? 'Go to home' : 'Create free account'}
          </button>
        </div>

        {/* Premium */}
        <div className="relative bg-botanica-green rounded-3xl p-8 overflow-hidden">
          <Leaf className="absolute -top-6 -right-6 w-32 h-32 text-white/10 rotate-12" />
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-semibold text-botanica-green bg-botanica-greenLight px-3 py-1 rounded-full mb-5">
            <Sparkles className="w-3 h-3" /> 7-day free trial
          </span>
          <div className="relative">
            <span className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center mb-5">
              <Crown className="w-5 h-5 text-white" />
            </span>
            <h2 className="font-serif text-2xl text-white mb-1">Premium</h2>
            <p className="text-sm text-white/75 mb-5">For families who want it all.</p>
            <div className="mb-6">
              <span className="font-serif text-4xl text-white">$1.99</span>
              <span className="text-sm text-white/75"> / month</span>
            </div>
            <ul className="space-y-3 mb-8">
              {premiumPerks.map((p) => (
                <li key={p} className="flex items-start gap-2.5 text-sm text-white">
                  <Check className="w-4 h-4 text-botanica-greenLight mt-0.5 shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <button
              onClick={handlePremium}
              className="w-full py-3 rounded-full bg-white text-botanica-greenDark font-semibold hover:bg-botanica-greenLight transition-colors"
            >
              {user ? 'Start free trial' : 'Sign up & start trial'}
            </button>
          </div>
        </div>
      </div>

      <p className="text-xs italic text-botanica-muted text-center mt-10">
        Educational only. Not medical advice. Cancel anytime — no questions asked.
      </p>
    </div>
  );
}
